import React, { useContext, useMemo } from 'react';
import Box from 'Components/Atoms/Box';
import './styles.scss';
import { CurrentStatus, Reservation } from 'types/reservations';
import Typography from 'Components/Atoms/Typography';
import {
  blockToTime,
  capitalizeWord,
  classHelper,
  dateHelper,
  noop,
  timeHelper,
  timeToBlock,
  toEuropeanDate,
  toCurrencyString,
} from 'utils/helper';
import { Ticket, WarningBar, WarningRounded } from 'Components/Atoms/Icons';
import AttrIcons from './Components/AttrIcons';
import ActionButtons from './Components/ActionButtons';
import {
  ReservationActionTypes,
  ReservationContext,
  useReservationContext,
} from 'Contexts/ReservationContext';
import { useHistory } from 'react-router';
import useIsLightReservations from 'CustomHooks/useIsLightReservations';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import PermPhoneMsgRounded from '@material-ui/icons/PermPhoneMsgRounded';
import RecordVoiceOverRounded from '@material-ui/icons/RecordVoiceOverRounded';
import useRestaurant from 'CustomHooks/useRestaurant';
import Button from 'Components/Atoms/Button';
import { stringifyCustomDataValue } from '../ReservationSideBar/Components/ReservationDetails';
import { hasRecentDragGesture, useDrag } from 'CustomHooks/useDragAndDrop';
import { DragPreviewImage } from 'react-dnd';
import Warning from '../Warning';

export type ReservationCardProps = {
  reservation: Reservation;
  active: boolean;
  hasWarnings: boolean;
  hideOccassion: boolean;
  handleAction: (
    id: string,
    type: ReservationActionTypes,
    payload?: any,
  ) => void;
  onClick?: (id: string | null) => void;
  occassions?: { title: string; id: string }[];
  isAnotherDay?: boolean;
};

type ReservationTicket = NonNullable<Reservation['ticket']>;
type ReservationTicketPayment = NonNullable<
  ReservationTicket['payments']
>[number] & {
  service_fee?: number;
};

const failedTicketPaymentStatuses = ['canceled', 'failed', 'expired'];
const settledTicketPaymentStatuses = ['paid', 'authorized'];
const onlineTicketPaymentMethods = ['mollie', 'stripe'];

const getTicketNumber = (value: unknown) =>
  typeof value === 'number' && !Number.isNaN(value) ? value : 0;

const getSettledTicketPaymentAmount = (payment: ReservationTicketPayment) => {
  if (!settledTicketPaymentStatuses.includes(String(payment.status))) return 0;

  const amount = getTicketNumber(payment.amount);

  if (onlineTicketPaymentMethods.includes(String(payment.paidVia))) {
    return Math.max(amount - getTicketNumber(payment.service_fee), 0);
  }

  return amount;
};

const getTicketPendingAmount = (ticket: ReservationTicket) => {
  const coveredAmount = (ticket.payments ?? []).reduce(
    (sum, payment) =>
      sum + getSettledTicketPaymentAmount(payment as ReservationTicketPayment),
    0,
  );

  return Math.max(getTicketNumber(ticket.amount) - coveredAmount, 0);
};

const getTicketPaymentBadge = (reservation: Reservation) => {
  const ticket = reservation.ticket;

  if (!ticket) {
    if (
      reservation.ticketId &&
      reservation.validTill &&
      reservation.validTill > Date.now()
    ) {
      return {
        status: 'open' as const,
        label: 'Ticketzahlung offen',
      };
    }

    return null;
  }

  const pendingAmount = getTicketPendingAmount(ticket);

  if (pendingAmount <= 0.009 || ticket.paid || ticket.status === 'succedded') {
    return null;
  }

  const latestPayment = (ticket.payments ?? [])
    .slice()
    .sort(
      (a, b) => getTicketNumber(b.createdAt) - getTicketNumber(a.createdAt),
    )[0];
  const latestPaymentFailed = failedTicketPaymentStatuses.includes(
    String(latestPayment?.status),
  );
  const formattedAmount = toCurrencyString(pendingAmount, '€', false, 1);

  if (ticket.status === 'failed' || latestPaymentFailed) {
    return {
      status: 'failed' as const,
      label: `Ticketzahlung fehlgeschlagen${
        formattedAmount ? `: ${formattedAmount} offen` : ''
      }`,
    };
  }

  return {
    status: 'open' as const,
    label: `Ticketzahlung offen${
      formattedAmount ? `: ${formattedAmount}` : ''
    }`,
  };
};

const RESERVATION_CARD_DRAG_THRESHOLD_PX = 8;
const RESERVATION_CARD_CLICK_SUPPRESS_MS = 500;

function cleanCustomData(
  customData: Reservation['customData'],
  imported = false,
  disabledFields?: string,
) {
  return Object.entries(customData ?? {}).filter(([key]) => {
    let hideWhenImported = [
      'Art',
      'Bereiche',
      'Nachname',
      'Stornierungsgebühr',
      'E-Mail',
      'Vorname',
      'Datum',
      'Ende',
      'Beginn',
      'Tischnummer',
      'Reservierungsnr.',
      'Erstellungsdatum',
      'Personen',
      'Booking channel',
      'Booking taken by',
      'Booking taken',
      'Kundennr.',
      'Sprache',
      'Überprüft',
      'Abw. Verweilzeit',
      'Telefon',
      'Abweichende Verweildauer',
      'Bearbeiter',
      'Verspätet um',
    ];

    if (imported) {
      return !hideWhenImported.includes(key) && !disabledFields?.includes(key);
    }

    if (disabledFields?.includes(key)) return false;

    return !['customStatus'].includes(key);
  });
}

const ReservationCard = ({
  reservation,
  active,
  hasWarnings,
  hideOccassion,
  handleAction = noop,
  onClick = noop,
  occassions = [],
  isAnotherDay = false,
}: ReservationCardProps) => {
  const {
    id = '',
    currentStatus = '',
    time = '',
    guests = 0,
    guest,
    occassion = '',
    tables = [],
    date = '',
    feedbackRating,
    experienceVoucher,
  } = reservation;

  const { getAddOnOrders, Shuffle, currentTime, openCommentModal } =
    useReservationContext();

  const [loading, setloading] = React.useState(false);

  const addOnOrders = useMemo(() => {
    return reservation?.orders ?? getAddOnOrders(id, false);
  }, [getAddOnOrders, id]);
  const hasOpenAddOn = addOnOrders.some((o) =>
    o.cart?.some((item) => !item.refunded),
  );

  const { reservationSettings, experimentalFlags } = useRestaurant();

  const showReservationLength = !!reservationSettings?.showReservationLength;

  const history = useHistory();

  const coloredLogo = useMemo(() => {
    return ['none', 'done', 'failed', 'normal'].includes(currentStatus);
  }, [currentStatus, active]);

  const pressTimer = React.useRef<NodeJS.Timeout | null>(null);
  const pointerStartRef = React.useRef<{
    x: number;
    y: number;
    pointerId: number;
  } | null>(null);
  const suppressNextClickRef = React.useRef(false);

  const clearPressTimer = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (
      suppressNextClickRef.current ||
      hasRecentDragGesture(RESERVATION_CARD_CLICK_SUPPRESS_MS)
    ) {
      event.preventDefault();
      event.stopPropagation();
      suppressNextClickRef.current = false;
      clearPressTimer();
      return;
    }

    clearPressTimer();

    if (isMobile) {
      onClick(id);
    } else {
      if (active) {
        onClick(null);
      } else {
        onClick(id);
      }
    }
  };

  const handleStart = (e: React.MouseEvent | React.TouchEvent) => {
    pressTimer.current = setTimeout(() => {
      openCommentModal(
        id,
        reservation?.guest?.hostComment ?? '',
        'hostComment',
      );
      pressTimer.current = null;
    }, 1000); // 1 second threshold
  };

  const handleTouchEnd = () => {
    clearPressTimer();
  };

  const lightReservations = useIsLightReservations();

  const occassionString =
    occassions?.find((o) => o.id === occassion)?.title || occassion;

  const goToReservation = () =>
    history.push(
      `/${reservation.restaurant}/reservations/${date}/${
        lightReservations ? 'reservationList' : 'tablePlan'
      }?reservationId=${reservation.id}`,
    );

  const isMobile = useMediaQuery('(max-width: 450px)');

  const nameStr = `${
    experimentalFlags.data?.showCode ? `#${reservation.customId} ` : ''
  }${guest.name} ${guest.company ? `(${guest.company})` : ''}`;
  const dragPreviewName =
    nameStr.length > 32 ? `${nameStr.slice(0, 32)}...` : nameStr;

  const hasPayment =
    !!reservation.minimumConsumption ||
    !!reservation.ticketId ||
    hasOpenAddOn ||
    !!reservation.hasAddOnOrders ||
    !!experienceVoucher;
  const ticketPaymentBadge = getTicketPaymentBadge(reservation);
  const showNamePaymentBadge =
    hasPayment && !nameStr.startsWith('Wird gerade erstellt');
  const nameClassName = classHelper([
    showNamePaymentBadge
      ? ticketPaymentBadge
        ? `reservation-payment-name-badge reservation-payment-name-badge--${ticketPaymentBadge.status} status--${reservation.currentStatus}`
        : 'text-minimum-consumption'
      : !coloredLogo
      ? 'text-white'
      : '',
  ]);

  const additionalButtons = useMemo(() => {
    if (isAnotherDay || isMobile || reservation.date !== dateHelper())
      return [];

    if (reservation.partlySeated) {
      return [
        {
          label: 'Vollständig Platzieren',
          type: ReservationActionTypes.FULLY_SEAT,
        },
      ];
    } else if (
      reservation.currentStatus === CurrentStatus.OVERDUE ||
      reservation.currentStatus === CurrentStatus.UPCOMING ||
      (reservation.startTimeInBlocks - 2 <=
        timeToBlock(currentTime || timeHelper()) &&
        reservation.currentStatus === CurrentStatus.NORMAL)
    ) {
      return [
        {
          label: 'Teilw. Platzieren',
          type: ReservationActionTypes.PARTLY_SEAT,
          hide: reservation.guests < 2,
        },
        {
          label: '+15 Min',
          type: ReservationActionTypes.MARK_AS_LATE,
          hide: experimentalFlags?.data?.hidePlusFifteen,
        },
      ].filter((x) => !x.hide);
    }
  }, [reservation, loading]);

  const dragDisabled =
    isMobile ||
    isAnotherDay ||
    reservation.done ||
    reservation.status === 'failed';
  const dragAndDropDisabled = !!experimentalFlags?.data?.disableDragAndDrop;

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    pointerStartRef.current = {
      x: event.clientX,
      y: event.clientY,
      pointerId: event.pointerId,
    };
    suppressNextClickRef.current = false;
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = pointerStartRef.current;
    if (!start || start.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;

    if (
      deltaX * deltaX + deltaY * deltaY >
      RESERVATION_CARD_DRAG_THRESHOLD_PX * RESERVATION_CARD_DRAG_THRESHOLD_PX
    ) {
      suppressNextClickRef.current = true;
      clearPressTimer();
    }
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (pointerStartRef.current?.pointerId === event.pointerId) {
      pointerStartRef.current = null;
    }
  };

  const currentCustomStatus = reservation?.customData?.customStatus
    ? experimentalFlags?.data?.currentTableStati?.find(
        (x) => x.title === reservation?.customData?.customStatus,
      )
    : null;

  const { drag, opacity } = useDrag<{
    id: string;
    from: number;
    till: number;
    guests: number;
    origin: 'sidebar';
    preview: {
      title: string;
      subtitle: string;
      status: string;
      backgroundColor?: string;
    };
  }>(
    {
      id: reservation.id,
      from: reservation.startTimeInBlocks,
      till: reservation.endTimeInBlocks,
      guests: reservation.guests,
      origin: 'sidebar',
      preview: {
        title: dragPreviewName,
        subtitle: `${time} - ${blockToTime(
          reservation.endTimeInBlocks,
        )} | ${guests} PAX`,
        status: currentStatus || 'normal',
        backgroundColor: currentCustomStatus?.color,
      },
    },
    'reservation',
    dragDisabled,
  );

  const cardDragRef = !dragAndDropDisabled ? drag : null;

  // Distinguish "payment abandoned" from "cancelled by staff/guest" visually.
  // Both are status === 'failed' under the hood, but abandoned reservations
  // represent guests who simply didn't finish payment and should not be
  // shown as actively cancelled (different color + "Abgebrochen" tooltip).
  const isAbandoned =
    (reservation as any)?.cancelationType === 'payment_abandoned';
  const hasSeatConflict =
    !!(reservation as any)?.paymentArrivedButNoSeat &&
    reservation.status !== 'failed';

  // Grace-period marker: the reservation is still pending the Mollie ticket
  // payment (validTill > 0, status=success, has ticket). The table is held
  // but the guest has not paid yet. Staff sees a subdued striped look with a
  // tooltip so they can distinguish this from a normal confirmed reservation.
  const inPaymentGracePeriod =
    !!reservation.ticketId &&
    !!reservation.validTill &&
    reservation.validTill > Date.now() &&
    reservation.status === 'success' &&
    !isAbandoned;

  const paymentStatusMessage = isAbandoned
    ? 'Zahlung nicht abgeschlossen'
    : hasSeatConflict
    ? 'Zahlung eingegangen, aber kein Tisch verfügbar – bitte manuell platzieren'
    : inPaymentGracePeriod
    ? 'Zahlung ausstehend – Tisch ist vorgemerkt bis die Zahlung eingeht'
    : null;

  return (
    <Box
      underline
      id={id}
      elevation={0}
      className={`reservation-card resa-${currentStatus} ${
        active ? 'active' : ''
      } ${reservation.partlySeated ? 'partly-seated' : ''} ${
        isAbandoned ? 'resa-abandoned' : ''
      } ${hasSeatConflict ? 'resa-no-seat' : ''} ${
        inPaymentGracePeriod ? 'resa-grace-period' : ''
      }`}
      style={
        !!currentCustomStatus
          ? {
              opacity,
              backgroundColor: active
                ? currentCustomStatus.chairColor
                : currentCustomStatus.color,
            }
          : { opacity }
      }
      onClick={!isAnotherDay ? handleClick : goToReservation}
      onMouseDown={
        !!experimentalFlags?.data?.disableDragAndDrop ? handleStart : undefined
      }
      onTouchStart={
        !!experimentalFlags?.data?.disableDragAndDrop ? handleStart : undefined
      }
      onTouchEnd={
        !!experimentalFlags?.data?.disableDragAndDrop
          ? handleTouchEnd
          : undefined
      }
      onMouseUp={
        !!experimentalFlags?.data?.disableDragAndDrop
          ? handleTouchEnd
          : undefined
      }
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      ref={cardDragRef}
    >
      <Box className="space-between">
        <Typography
          variant="text-4"
          block
          className={coloredLogo ? 'text-subdued' : 'text-white'}
          style={{ marginTop: 4 }}
        >
          {isAnotherDay ? toEuropeanDate(date) + ' - ' : ''}
          {time}
          {!!reservation.customData?.['Verspätet um'] && (
            <>
              {' '}
              <small
                style={{
                  padding: 2,
                  borderRadius: 2,
                  background: 'var(--color-warning)',
                  color: 'black',
                }}
              >
                +{reservation.customData?.['Verspätet um']} Min
              </small>
            </>
          )}{' '}
          {showReservationLength
            ? `- ${blockToTime(
                timeToBlock(time) + reservation.reservationLength,
              )}`
            : ''}{' '}
          • {guests} Pax
        </Typography>
        <AttrIcons
          customAttr={guest.customAttr ?? []}
          attr={[
            ...(guest?.attr || []),
            guest?.comment || guest?.hostComment ? 'comment' : '',
            !!reservation.ticketId ? 'ticket' : '',
            !!reservation.minimumConsumption ? 'ticket' : '',
          ]}
          fixed={reservation.fixed}
          emailFailed={reservation.emailStatus === 'failed'}
          coloredLogo={coloredLogo}
          feedbackRating={feedbackRating}
          done={!!reservation.done}
          creditCardCustomerId={guest.creditCardCustomerId}
          noShow={!!reservation.noShow}
          noShowFee={reservation.noShowFee}
          addOns={hasOpenAddOn || !!reservation.hasAddOnOrders}
        />
      </Box>
      <Box
        className="space-between reservation-card-title-row"
        style={{ minHeight: 36 }}
      >
        <Typography
          variant="text-3"
          weight="bold"
          block
          className={classHelper(['reservation-card-name', nameClassName])}
          title={ticketPaymentBadge?.label || nameStr}
          style={{
            marginTop: 3,
          }}
        >
          <span className="reservation-card-name-text">
            {!!experienceVoucher && '🎁 '}
            {currentCustomStatus?.icon ?? ''}
            {nameStr}
            {!!reservation.noShowConfirmation?.validTill &&
              ' (Kreditkarte f.)'}
          </span>
          {reservation.source === 'phone' &&
            (reservation.additional?.voiceApi ? (
              <RecordVoiceOverRounded
                fontSize="inherit"
                style={{ marginLeft: 4 }}
                titleAccess="Voice-Agent"
              />
            ) : (
              <PermPhoneMsgRounded fontSize="inherit" style={{ marginLeft: 4 }} />
            ))}
        </Typography>
        {!isAnotherDay && !isMobile && (
          <ActionButtons
            {...{
              id,
              handleAction: (id, type, payload) => {
                if (pressTimer.current) {
                  clearTimeout(pressTimer.current);
                  pressTimer.current = null;
                }
                handleAction(id, type, payload);
              },
              coloredLogo,
              currentStatus,
              reservation,
            }}
          />
        )}
      </Box>
      {!!paymentStatusMessage && (
        <Box
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography
            variant="text-4"
            block
            weight="bold"
            className={coloredLogo ? 'text-subdued' : 'text-white'}
          >
            {paymentStatusMessage}
          </Typography>
        </Box>
      )}
      {(!!Shuffle.state || reservationSettings?.showCommentInResaCard) && (
        <Box style={{ marginBottom: 4 }}>
          {!!reservation.guest.comment && (
            <Box>
              <Typography
                variant="text-4"
                block
                weight="bold"
                className={coloredLogo ? 'text-subdued' : 'text-white'}
              >
                Kommentar
              </Typography>
              <Typography
                variant="text-4"
                block
                className={coloredLogo ? 'text-subdued' : 'text-white'}
              >
                {!experimentalFlags?.data?.truncateComments
                  ? reservation.guest.comment
                  : reservation.guest.comment.slice(0, 80)}
              </Typography>
            </Box>
          )}
          {!!reservation.guest.hostComment && (
            <Box>
              <Typography
                variant="text-4"
                block
                weight="bold"
                className={coloredLogo ? 'text-subdued' : 'text-white'}
              >
                Service Kommentar
              </Typography>
              <Typography
                variant="text-4"
                block
                className={coloredLogo ? 'text-subdued' : 'text-white'}
              >
                {!experimentalFlags?.data?.truncateComments
                  ? reservation.guest.hostComment
                  : reservation.guest.hostComment.slice(0, 80)}
              </Typography>
            </Box>
          )}
          {!!reservation.guest.guestComment && (
            <Box>
              <Typography
                variant="text-4"
                block
                weight="bold"
                className={coloredLogo ? 'text-subdued' : 'text-white'}
              >
                Langzeit Kommentar
              </Typography>
              <Typography
                variant="text-4"
                block
                className={coloredLogo ? 'text-subdued' : 'text-white'}
              >
                {!experimentalFlags?.data?.truncateComments
                  ? reservation.guest.guestComment
                  : reservation.guest.guestComment.slice(0, 80)}
              </Typography>
            </Box>
          )}
          {cleanCustomData(
            reservation.customData ?? {},
            !!reservation.additional?.imported,
            experimentalFlags?.data?.disabledCustomFields,
          )
            .filter((x) => !!stringifyCustomDataValue(x[1]))
            .map(([key, value]) => (
              <Box
                style={
                  String(stringifyCustomDataValue(value)).length < 100
                    ? {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }
                    : {}
                }
              >
                <Typography
                  variant="text-4"
                  block
                  weight="bold"
                  className={coloredLogo ? 'text-subdued' : 'text-white'}
                >
                  {key === 'balance'
                    ? 'Bisher bestellt'
                    : key.replace('_internal', '')}
                </Typography>
                <Typography
                  variant="text-4"
                  block
                  className={coloredLogo ? 'text-subdued' : 'text-white'}
                >
                  {stringifyCustomDataValue(value, key)}
                </Typography>
              </Box>
            ))}
        </Box>
      )}
      <Box className="space-between flex-end">
        <Box style={{ paddingBottom: 4 }} flex>
          {isAnotherDay && (
            <>
              <WarningRounded
                style={
                  coloredLogo
                    ? { color: '#CCC910', fontSize: '14px' }
                    : { color: 'white', fontSize: '14px' }
                }
              />
              <Typography
                variant="text-4"
                className={coloredLogo ? 'text-subdued' : 'text-white'}
                style={{ marginRight: 3 }}
                translation="reservations"
              >
                Wrong Date •
              </Typography>
            </>
          )}
          {(!!reservation.stammtischId || !!reservation.regularTableId) && (
            <Typography
              variant="text-4"
              className="text-white"
              style={
                coloredLogo
                  ? { marginRight: 3, color: 'var(--color-tertiary)' }
                  : { marginRight: 3 }
              }
            >
              Stammtisch •
            </Typography>
          )}
          {!!reservation.ticketId && (
            <Typography
              variant="text-4"
              className="text-white"
              style={
                coloredLogo
                  ? { marginRight: 3, color: 'var(--color-tertiary)' }
                  : { marginRight: 3 }
              }
            >
              Ticket •
            </Typography>
          )}
          {!!reservation.minimumConsumption && (
            <Typography
              variant="text-4"
              className="text-white"
              style={
                coloredLogo
                  ? { marginRight: 3, color: 'var(--color-secondary)' }
                  : { marginRight: 3 }
              }
            >
              Min. Verzehr •
            </Typography>
          )}
          {!hideOccassion && (
            <Typography
              variant="text-4"
              className={coloredLogo ? 'text-subdued' : 'text-white'}
              style={{ marginRight: 3 }}
            >
              {occassionString.slice(0, 25)} •
            </Typography>
          )}
          {!!experienceVoucher && (
            <Typography
              variant="text-4"
              className="text-white"
              style={
                coloredLogo
                  ? { marginRight: 3, color: 'var(--color-tertiary)' }
                  : { marginRight: 3 }
              }
            >
              Gutschein •
            </Typography>
          )}
          <Typography
            variant="text-4"
            className={coloredLogo ? 'text-subdued' : 'text-white'}
            translation="reservations"
            style={
              tables?.length && hideOccassion
                ? {
                    fontSize: '0.9rem',
                    fontWeight: 'bold',
                  }
                : {}
            }
          >
            {currentStatus === 'failed'
              ? 'Canceled'
              : (experimentalFlags?.data?.truncateTableNames
                  ? `${tables?.[0]}${tables?.[1] ? '+' : ''}`
                  : tables?.join(', ')) || 'No Tables'}
          </Typography>
        </Box>

        <Typography
          variant="text-5"
          style={{
            color: coloredLogo ? 'var(--color-tertiary)' : 'white',
          }}
        >
          {reservation.position && reservation.position + '.'}
        </Typography>
      </Box>

      <Warning
        id={id}
        hide
        style={{
          padding: 0,
          paddingTop: 8,
        }}
      />

      {!!additionalButtons?.length && (
        <Box className="flex" style={{ margin: '4px 0', gap: 8 }}>
          {additionalButtons?.map((button, index) => (
            <Button
              key={index}
              className={classHelper([
                'res-additional-button',
                coloredLogo && 'uncolored',
              ])}
              onClick={async (e) => {
                e.stopPropagation();
                if (pressTimer.current) {
                  clearTimeout(pressTimer.current);
                  pressTimer.current = null;
                }
                if (loading) return;
                setloading(true);
                await handleAction(id, button.type);
                setloading(false);
              }}
              style={{
                border: coloredLogo
                  ? '2px solid var(--color-border)'
                  : '2px solid #ffffff',
                background: 'transparent',
                color: coloredLogo
                  ? 'var(--color-text-sub)'
                  : 'rgba(255,255,255,0.8)',
              }}
              loading={loading}
            >
              {button.label}
            </Button>
          ))}
        </Box>
      )}

      {hasWarnings && <WarningBar />}
    </Box>
  );
};

export default ReservationCard;
